<template>
  <div class="relative">
    <button
      ref="buttonRef"
      @click="handleClick"
      @mouseenter="showPreview = true"
      @mouseleave="showPreview = false"
      :class="[
        'flex items-center justify-center rounded-md border-2 transition-all',
        'h-10 w-10',
        hasChanges
          ? 'border-orange-500 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-600 dark:bg-orange-950 dark:text-orange-300 dark:hover:bg-orange-900'
          : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-slate-900 dark:text-gray-400 dark:hover:bg-slate-800',
        !isAdmin && (syncHappening || !currentTeam) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      ]"
      :disabled="!isAdmin && (syncHappening || !currentTeam)"
      :title="!hasChanges ? tooltipText : ''"
    >
      <!-- Show check icon when no changes -->
      <CheckIcon v-if="!hasChanges" class="h-5 w-5" />

      <!-- Show change count when there are changes -->
      <span v-else class="text-sm font-bold">
        {{ changeCount > 99 ? '99+' : changeCount }}
      </span>
    </button>

    <!-- Hover preview -->
    <PublishChangesPreview
      v-if="hasChanges"
      :show="showPreview"
      :changes="configChanges"
      :button-ref="buttonRef"
      position="left" />

    <!-- Modals (same as PublishButton) -->
    <PublishErrors
      :visible="publishErrorsVisible"
      @update:visible="publishErrorsVisible = $event"
      :validationErrors="validationErrors" />
    <PublishChangesModal
      :visible="publishChangesModalVisible"
      @update:visible="publishChangesModalVisible = $event"
      :changes="configChanges"
      :is-publishing="isPublishingChanges"
      @publish="handlePublishWithMessage" />
  </div>
</template>

<script setup lang="ts">
import { CheckIcon } from '@heroicons/vue/24/outline'
import { useConfigChanges } from '@/composables/useConfigChanges'
import type { ConfigChange } from '@/composables/useConfigChanges'
import PublishErrors from '@/components/common/PublishErrors.vue'
import PublishChangesModal from '@/components/common/PublishChangesModal.vue'
import PublishChangesPreview from '@/components/common/PublishChangesPreview.vue'
import { useIsFetching } from '@tanstack/vue-query'
import { watchDebounced } from '@vueuse/core'

const authStore = useAuthStore()
const { isAdmin } = storeToRefs(authStore)

const isDemo = inject('isDemo', false)

const configStore = useConfigStore()
const { config, fullResponse, currentTeam, isUpdatingConfig } = storeToRefs(configStore)
const { publish } = configStore

const isFetching = useIsFetching()

const { analyzeChanges } = useConfigChanges()
const configChanges = ref<ConfigChange[]>([])
const validationErrors = ref([])
const publishErrorsVisible = ref(false)
const publishChangesModalVisible = ref(false)
const isPublishingChanges = ref(false)
const showPreview = ref(false)
const buttonRef = ref<HTMLElement | null>(null)

// Analyze changes between draft and published config
const analyzeConfigChanges = async () => {
  if (!config.value || !fullResponse.value) {
    configChanges.value = []
    return
  }

  const publishedConfig = fullResponse.value.stable || null
  configChanges.value = await analyzeChanges(config.value, publishedConfig)
}

// Watch for config changes with debouncing to avoid excessive analysis during rapid edits
watchDebounced(
  [config, fullResponse],
  () => {
    analyzeConfigChanges()
  },
  { debounce: 1000, deep: true, immediate: true }
)

const syncHappening = computed(() => {
  return isFetching.value > 0 || isUpdatingConfig.value
})

const changeCount = computed(() => configChanges.value.length)
const hasChanges = computed(() => changeCount.value > 0)

const tooltipText = computed(() => {
  if (!hasChanges.value) {
    return 'All changes published'
  }
  const count = changeCount.value
  return `${count} unpublished ${count === 1 ? 'change' : 'changes'} - click to review`
})

const handleClick = async () => {
  if (isDemo.value) {
    alert(`Drats! Publishing isn't available within the demo sandbox.`)
    return
  }

  if (!isAdmin.value && (syncHappening.value || !currentTeam.value)) return

  const errors = configStore.validateForPublish()

  if (errors.length > 0) {
    validationErrors.value = errors
    publishErrorsVisible.value = true
  } else {
    // Analyze changes and show modal
    await analyzeConfigChanges()
    publishChangesModalVisible.value = true
  }
}

const handlePublishWithMessage = async (message: string) => {
  isPublishingChanges.value = true
  try {
    await publish(message)
    // Wait a brief moment for the refetch to complete
    await new Promise(resolve => setTimeout(resolve, 500))
    publishChangesModalVisible.value = false
  } finally {
    isPublishingChanges.value = false
  }
}
</script>
